import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PoundSterling } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { RecordPaymentModal } from "@/components/portal/QuickActions";
import { EmptyState, Spinner, StatusBadge, gbp2 } from "@/components/portal/shared";

export default function PaymentsPage() {
  const { api } = usePortalAuth();
  const [orders, setOrders] = useState(null);
  const [pay, setPay] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (orders === null) return <Spinner />;

  const depOut = orders.reduce((s, o) => s + Math.max(0, (o.deposit_required || 0) - (o.deposit_paid || 0)), 0);
  const balOut = orders.reduce((s, o) => s + Math.max(0, (o.balance_due || 0) - (o.balance_paid || 0)), 0);
  const received = orders.reduce((s, o) => s + (o.deposit_paid || 0) + (o.balance_paid || 0), 0);

  const summary = [
    ["Deposits outstanding", depOut, "kpi-deposits-outstanding"],
    ["Balances outstanding", balOut, "kpi-balances-outstanding"],
    ["Payments received", received, "kpi-payments-received"],
  ];

  return (
    <div data-testid="payments-page" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {summary.map(([label, value, tid]) => (
          <div key={tid} className="rounded-2xl border border-warm-200 bg-white p-5 shadow-sm" data-testid={tid}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">{label}</p>
            <p className="font-serif text-3xl text-navy-900">{gbp2(value)}</p>
          </div>
        ))}
      </div>

      {orders.length === 0 ? (
        <EmptyState icon={PoundSterling}>No payments to track yet.</EmptyState>
      ) : (
        <div className="rounded-2xl border border-warm-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-warm-200 bg-warm-100/60">
                <th className="px-5 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Deposit</th>
                <th className="px-4 py-3">Balance</th>
                <th className="px-4 py-3">Outstanding</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-200">
              {orders.map((o) => {
                const dOut = Math.max(0, (o.deposit_required || 0) - (o.deposit_paid || 0));
                const bOut = Math.max(0, (o.balance_due || 0) - (o.balance_paid || 0));
                return (
                  <tr key={o.id} className="hover:bg-warm-100/50 transition-colors" data-testid={`payment-row-${o.id}`}>
                    <td className="px-5 py-3.5 font-semibold text-navy-900">{o.ref}</td>
                    <td className="px-4 py-3.5">
                      <Link to={`/portal/customers/${o.lead_id}?tab=payments`} className="font-medium text-slate-800 hover:underline">{o.customer}</Link>
                    </td>
                    <td className="px-4 py-3.5 font-semibold">{gbp2(o.total)}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-600">{gbp2(o.deposit_paid)} / {gbp2(o.deposit_required)}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-600">{gbp2(o.balance_paid)} / {gbp2(o.balance_due)}</td>
                    <td className={`px-4 py-3.5 font-semibold ${dOut + bOut > 0 ? "text-amber-700" : "text-emerald-700"}`}>{gbp2(dOut + bOut)}</td>
                    <td className="px-4 py-3.5"><StatusBadge s={o.status} /></td>
                    <td className="px-4 py-3.5">
                      {dOut + bOut > 0 && (
                        <button type="button" data-testid={`payment-record-${o.id}`} onClick={() => setPay(o)} className="inline-flex items-center gap-1 rounded-full bg-navy-900 px-3.5 py-1.5 text-[11px] font-semibold text-white hover:bg-navy-800">
                          Record
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {pay && <RecordPaymentModal order={pay} onClose={() => setPay(null)} onDone={load} />}
    </div>
  );
}
